/**
 * /admin/platform/custom-domains — register a CNAME, hand the
 * operator the verification token, then verify when DNS lands.
 */

import { type FormEvent, useEffect, useState } from "react";
import {
  adminApi,
  type AdminApiError,
  type CustomDomainSummary,
  type TenantSummary,
} from "./api";

export function CustomDomainsPage() {
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [tenantId, setTenantId] = useState("");
  const [hostname, setHostname] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [latest, setLatest] = useState<CustomDomainSummary | null>(null);
  const [verifyId, setVerifyId] = useState("");
  const [verifyDns, setVerifyDns] = useState("");
  const [verifyMessage, setVerifyMessage] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        const result = await adminApi.listTenants();
        setTenants(result.tenants);
        if (result.tenants.length > 0) {
          setTenantId(result.tenants[0].id);
        }
      } catch (exc) {
        const apiErr = exc as AdminApiError;
        setError(apiErr.message ?? "Could not load tenants.");
      }
    })();
  }, []);

  async function onRegister(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const result = await adminApi.registerCustomDomain(tenantId, hostname.trim());
      setLatest(result);
      setVerifyId(result.id);
      setHostname("");
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setError(apiErr.message ?? "Registration failed.");
    } finally {
      setBusy(false);
    }
  }

  async function onVerify(e: FormEvent) {
    e.preventDefault();
    setVerifyMessage(null);
    try {
      const result = await adminApi.verifyCustomDomain(verifyId, verifyDns.trim());
      setVerifyMessage(
        result.verified
          ? `Verified ${result.hostname} at ${result.verified_at ?? "now"}.`
          : "Verification did not succeed.",
      );
    } catch (exc) {
      const apiErr = exc as AdminApiError;
      setVerifyMessage(apiErr.message ?? "Verification failed.");
    }
  }

  return (
    <div className="admin-page">
      <header className="admin-page-head">
        <h1>Custom domains</h1>
        <p className="admin-page-sub">
          Map a hostname onto a tenant. The tenant owner sets the
          required DNS record; you confirm here once the record
          propagates. Production deploys will swap the host header to
          tenant resolution path through Cloudflare's edge.
        </p>
      </header>

      {error !== null && <div className="auth-error" role="alert">{error}</div>}

      <form className="admin-card" onSubmit={onRegister} noValidate>
        <h2 className="admin-card-title">Register hostname</h2>
        <div className="admin-inline-fields">
          <div className="field">
            <label htmlFor="cd-tenant">Tenant</label>
            <select
              id="cd-tenant"
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              required
            >
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} ({t.slug})
                </option>
              ))}
            </select>
          </div>
          <div className="field">
            <label htmlFor="cd-host">Hostname</label>
            <input
              id="cd-host"
              type="text"
              value={hostname}
              onChange={(e) => setHostname(e.target.value.toLowerCase())}
              placeholder="app.example.com"
              pattern="[a-z0-9.-]+\.[a-z]{2,}"
              required
            />
          </div>
        </div>
        <div className="actions">
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy}>
            {busy ? "Registering…" : "Register"}
          </button>
        </div>
      </form>

      {latest !== null && (
        <div className="admin-card">
          <h2 className="admin-card-title">Verification token issued</h2>
          <p className="field-hint">
            Copy the token below into a TXT record at{" "}
            <code>_cvp-verify.{latest.hostname}</code> (or per your DNS
            provider's instructions). Token expires{" "}
            {new Date(latest.verification_expires_at).toLocaleString()}.
          </p>
          <pre className="admin-token">{latest.verification_token}</pre>
        </div>
      )}

      <form className="admin-card" onSubmit={onVerify} noValidate>
        <h2 className="admin-card-title">Verify DNS record</h2>
        <div className="field">
          <label htmlFor="cv-id">Custom domain id</label>
          <input
            id="cv-id"
            type="text"
            value={verifyId}
            onChange={(e) => setVerifyId(e.target.value)}
            required
          />
        </div>
        <div className="field">
          <label htmlFor="cv-dns">
            Token value as it appears in DNS
          </label>
          <input
            id="cv-dns"
            type="text"
            value={verifyDns}
            onChange={(e) => setVerifyDns(e.target.value)}
            required
          />
        </div>
        <div className="actions">
          <button type="submit" className="btn btn-primary btn-sm">
            Verify
          </button>
        </div>
        {verifyMessage !== null && (
          <p className="field-hint">{verifyMessage}</p>
        )}
      </form>
    </div>
  );
}
